import { Layout } from "../components/Layout";
import { BackHome, SectionHeader, StatBlock, ProcessCard, FeatureCard, GreenCta } from "../components/Shared";
import { SeoText } from "../components/SeoText";
import { useSeo } from "../hooks/useSeo";
import {
  ArrowRight,
  MessageCircle,
  Zap,
  BookOpen,
  Users,
  Calendar,
  Mail,
  Moon,
  Phone,
  Target,
  CalendarCheck,
} from "lucide-react";
import { Link } from "react-router-dom";
import { WHATSAPP_LINK } from "../lib/api";

const stats = [
  { value: "30%", label: "Opportunités récupérées" },
  { value: "0", label: "Appel manqué" },
  { value: "48h", label: "Mise en place" },
  { value: "24/7", label: "Disponibilité" },
];

const capabilities = [
  { num: "01", Icon: Zap, title: "Répond instantanément", text: "Décroche dès la première sonnerie, sans répondeur ni attente." },
  { num: "02", Icon: BookOpen, title: "Connaît votre activité", text: "Maîtrise vos services, vos tarifs et vos disponibilités. Entraînée sur vos documents." },
  { num: "03", Icon: Users, title: "Qualifie les prospects", text: "Besoin, budget, urgence — trie pour vous les leads chauds." },
  { num: "04", Icon: Calendar, title: "Synchronise votre agenda", text: "RDV posé en direct dans Google Calendar ou Outlook. Zéro aller-retour." },
  { num: "05", Icon: Mail, title: "Confirmations SMS & email", text: "Rappel automatique 24h avant. Zéro no-show." },
  { num: "06", Icon: Moon, title: "Nuits · week-ends · fériés", text: "Capte les appels que vous n'auriez jamais reçus. Pendant que vos concurrents dorment, l'IA travaille." },
];

const Vocal = () => {
  useSeo({
    title: "Réceptionniste IA pour PME — Genève | Consulting IA",
    description: "Un agent vocal IA répond à vos clients, qualifie les demandes et gère vos rendez-vous — 24h/24, sans recruter. Déjà en production à Genève.",
    path: "/services/receptionniste-ia",
  });

  return (
    <Layout>
      <BackHome />

      <section className="px-6 pt-10 pb-12 md:pt-16 text-center">
        <div className="max-w-4xl mx-auto fade-in">
          <span className="pill-badge mb-5">🎙 Réceptionniste Vocal IA</span>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold title-text leading-[1.05] tracking-tight">
            Un réceptionniste IA qui répond <span className="accent-text">à vos clients 24h/24, 7j/7</span>
          </h1>
          <p className="body-text text-lg mt-7 leading-relaxed max-w-2xl mx-auto">
            Une réceptionniste qui ne dort jamais. Répond à chaque appel — nuits, week-ends
            et jours fériés inclus.
          </p>
          <div className="flex flex-wrap gap-2 mt-7 mb-9 justify-center">
            <span className="pill-badge">🇨🇭 Données hébergées en Suisse</span>
            <span className="pill-badge">🔒 Conforme LPD / FADP</span>
            <span className="pill-badge">⚡ Mise en place en 48h</span>
          </div>
          <div className="flex flex-col sm:flex-row gap-4 flex-wrap justify-center">
            <Link to="/contact" className="btn-primary" data-testid="vocal-demo-cta">
              Demander une démo gratuite <ArrowRight size={18} />
            </Link>
            <a href={WHATSAPP_LINK} target="_blank" rel="noopener noreferrer" className="btn-secondary">
              <MessageCircle size={18} /> WhatsApp
            </a>
          </div>
        </div>
      </section>

      <section className="px-6 pb-10">
        <div className="max-w-5xl mx-auto fade-in">
          <StatBlock stats={stats} testId="vocal-stats" />
        </div>
      </section>

      <section className="px-6 py-16">
        <div className="max-w-5xl mx-auto card-base fade-in" style={{ background: "#1a2744" }}>
          <span className="pill-badge-dark mb-4">Réalité du marché</span>
          <h2 className="text-white font-extrabold text-3xl md:text-4xl mb-4 leading-tight">
            Le problème que vous <span className="accent-text">connaissez bien.</span>
          </h2>
          <p className="text-white/80 text-lg leading-relaxed">
            Un prospect qui n'obtient pas de réponse ne rappelle pas.{" "}
            <span className="accent-text font-bold">Il appelle votre concurrent.</span>
          </p>
        </div>
      </section>

      <section className="px-6 py-16 section-alt">
        <div className="max-w-6xl mx-auto">
          <SectionHeader
            badge="Comment ça fonctionne"
            title="Six capacités"
            accentTitle="essentielles."
            subtitle="Pensées pour les PME suisses qui veulent capter chaque opportunité."
          />
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {capabilities.map((c) => <FeatureCard key={c.num} {...c} />)}
          </div>
        </div>
      </section>

      <section className="px-6 py-16">
        <div className="max-w-6xl mx-auto">
          <SectionHeader badge="Pour quels types de PME" title="Trois étapes." accentTitle="Aucun effort." />
          <div className="grid md:grid-cols-3 gap-6">
            <ProcessCard num="01" badge="3 minutes" Icon={Phone} title="Client appelle" text="À 22h, le dimanche ou en plein été — l'IA décroche immédiatement." />
            <ProcessCard num="02" badge="Instantané" Icon={Target} title="L'IA qualifie" text="Besoin, budget, urgence — la conversation reste naturelle et fluide." />
            <ProcessCard num="03" badge="Immédiat" Icon={CalendarCheck} title="RDV confirmé" text="Créneau réservé en direct dans votre agenda. SMS + email envoyés." />
          </div>
        </div>
      </section>

      <GreenCta
        badge="Prochaine étape"
        title="Réservez votre démo gratuite."
        subtitle="15 minutes pour découvrir comment l'IA peut décrocher à votre place — dès demain."
        primaryLabel={<>Demander une démo <ArrowRight size={18} /></>}
        primaryAction={() => (window.location.href = "/contact")}
        secondaryLabel="WhatsApp"
        secondaryAction={WHATSAPP_LINK}
        footnotes={["Démo gratuite", "Basé à Genève"]}
      />

      <SeoText>
        <h2 className="section-title mb-6">
          Ce que remplace un <span className="accent-text">réceptionniste vocal IA</span>
        </h2>
        <p className="body-text leading-relaxed mb-5">
          Le réceptionniste vocal IA est conçu pour les PME genevoises et suisses romandes
          qui perdent du chiffre d'affaires sur les appels non décrochés. Que vous dirigiez
          une fiduciaire, un cabinet médical ou dentaire, un garage, une agence
          immobilière, un service de transport VIP, un parking privé, une étude de notaire
          ou une activité indépendante, chaque appel manqué représente un prospect parti
          chez le concurrent.
        </p>
        <p className="body-text leading-relaxed mb-5">
          Notre solution remplace ou complète : un standard téléphonique externalisé
          coûteux, un répondeur automatique impersonnel, une réceptionniste à temps partiel,
          ou tout simplement le téléphone qui sonne dans le vide quand vous êtes en
          rendez-vous. L'agent vocal IA décroche dès la première sonnerie, comprend la
          demande en français (ou en anglais, allemand, italien selon vos besoins),
          qualifie le prospect en 2 à 3 minutes, et place directement un créneau dans
          votre agenda Google Calendar ou Outlook.
        </p>
        <p className="body-text leading-relaxed mb-5">
          La voix est naturelle, indistinguable d'une vraie réceptionniste pour la
          majorité des appelants. L'IA est entraînée spécifiquement sur vos documents
          internes : vos services, vos tarifs, vos disponibilités, vos conditions, vos
          FAQ. Elle peut transférer l'appel à un humain si la demande est complexe, ou
          vous envoyer un résumé écrit par email et SMS à la fin de chaque conversation.
        </p>
        <p className="body-text leading-relaxed mb-5">
          La mise en place se fait en 48 heures sur votre numéro existant — pas besoin de
          changer de ligne téléphonique. Les appels sont enregistrés (avec consentement
          conformément à la LPD), les données sont hébergées en Suisse, et vous gardez le
          contrôle total : tableaux de bord en temps réel, statistiques de qualification,
          taux de transformation. Les premiers clients qui ont déployé cette solution à
          Genève ont récupéré entre 25 % et 30 % d'opportunités auparavant perdues.
        </p>
        <p className="body-text leading-relaxed">
          Le tarif est volontairement simple : CHF 900 de mise en place, puis CHF 200 par
          mois tout inclus (lignes, IA, agenda, SMS de confirmation, support WhatsApp
          direct avec moi). Aucun engagement de durée. Demandez une démo gratuite et
          écoutez la voix de votre futur réceptionniste IA en moins de 15 minutes.
        </p>
      </SeoText>
    </Layout>
  );
};

export default Vocal;
